import { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import "./i18n";
import {
  GraduationCap,
  DownloadSimple,
  Calendar,
  Lightbulb,
  LinkSimple,
  ChartLineUp,
  WarningCircle,
  Receipt,
  UserCircle,
  Headset,
  UsersThree,
} from "@phosphor-icons/react";

import { AuthProvider } from "./contexts/AuthContext";
import useRobotsMeta from "./hooks/useRobotsMeta";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import PortalLayout from "./components/portal/PortalLayout";
import ManagementGuard from "./components/portal/ManagementGuard";
import TrackAccessGuard from "./components/portal/TrackAccessGuard";
import Home from "./pages/Home";
import Programme from "./pages/Programme";
import Gestion from "./pages/Gestion";
import Contact from "./pages/Contact";
import Login from "./pages/EspaceEtudiant";
import LegalPlaceholder from "./pages/legal/LegalPlaceholder";
import LegalNotice from "./pages/legal/LegalNotice";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import PortalHome from "./pages/portal/PortalHome";
import PortalInvoices from "./pages/portal/PortalInvoices";
import PortalPlaceholder from "./pages/portal/PortalPlaceholder";
import PortalCourses from "./pages/portal/PortalCourses";
import PortalThemeView from "./pages/portal/PortalThemeView";
import PortalChapterReader from "./pages/portal/PortalChapterReader";
import PortalResources from "./pages/portal/PortalResources";
import PortalInspirations from "./pages/portal/PortalInspirations";
import PortalInspirationReader from "./pages/portal/PortalInspirationReader";
import PortalLeaks from "./pages/portal/PortalLeaks";
import PortalLeakDetail from "./pages/portal/PortalLeakDetail";
import AdminLayout from "./components/admin/AdminLayout";
import AdminHome from "./pages/admin/AdminHome";
import AdminPlaceholder from "./pages/admin/AdminPlaceholder";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminTheme from "./pages/admin/AdminTheme";
import AdminChapterEditor from "./pages/admin/AdminChapterEditor";
import AdminResources from "./pages/admin/AdminResources";
import AdminInspirations from "./pages/admin/AdminInspirations";
import AdminInspirationEditor from "./pages/admin/AdminInspirationEditor";
import AdminLeaks from "./pages/admin/AdminLeaks";
import AdminLeakDetail from "./pages/admin/AdminLeakDetail";
import AdminInbox from "./pages/admin/AdminInbox";
import AdminInboxDetail from "./pages/admin/AdminInboxDetail";
import AdminModels from "./pages/admin/AdminModels";
import AdminModelLayout from "./pages/admin/models/AdminModelLayout";
import AdminModelProfile from "./pages/admin/models/AdminModelProfile";
import AdminModelNotes from "./pages/admin/models/AdminModelNotes";
import AdminModelLeaks from "./pages/admin/models/AdminModelLeaks";
import AdminModelTabPlaceholder from "./pages/admin/models/AdminModelTabPlaceholder";
import AdminModelFormation from "./pages/admin/models/AdminModelFormation";
import AdminModelPlanning from "./pages/admin/models/AdminModelPlanning";
import PortalPlanning from "./pages/portal/PortalPlanning";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const RobotsMetaSwitcher = () => {
  useRobotsMeta();
  return null;
};

const PORTAL_ROUTES = ["/login", "/portal", "/admin"];
const isPortalRoute = (pathname) =>
  PORTAL_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const Chrome = ({ children }) => {
  const { pathname } = useLocation();
  if (isPortalRoute(pathname)) {
    return <>{children}</>;
  }
  return (
    <>
      <Header />
      {children}
      <Footer />
    </>
  );
};

function App() {
  return (
    <div className="App font-body antialiased bg-white text-slate-900">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <RobotsMetaSwitcher />
          <Chrome>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/program" element={<Programme />} />
              <Route path="/management" element={<Gestion />} />
              <Route path="/contact" element={<Contact />} />
              <Route
                path="/legal"
                element={<LegalNotice />}
              />
              <Route
                path="/privacy"
                element={<PrivacyPolicy />}
              />
              <Route
                path="/terms"
                element={
                  <LegalPlaceholder
                    testId="terms-page"
                    titleKey="footer.terms"
                    kickerKey="legal.kicker_drafting"
                    messageKey="legal.in_drafting"
                  />
                }
              />
              <Route
                path="/sales-terms"
                element={
                  <LegalPlaceholder
                    testId="sales-terms-page"
                    titleKey="footer.sales_terms"
                    kickerKey="legal.kicker_drafting"
                    messageKey="legal.in_drafting"
                  />
                }
              />
              <Route path="/login" element={<Login />} />

              <Route
                path="/portal"
                element={
                  <ProtectedRoute>
                    <PortalLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<PortalHome />} />
                <Route path="courses" element={<PortalCourses />} />
                <Route
                  path="courses/:track/:themeSlug"
                  element={
                    <TrackAccessGuard>
                      <PortalThemeView />
                    </TrackAccessGuard>
                  }
                />
                <Route
                  path="courses/:track/:themeSlug/:chapterSlug"
                  element={
                    <TrackAccessGuard>
                      <PortalChapterReader />
                    </TrackAccessGuard>
                  }
                />
                <Route path="resources" element={<PortalResources />} />
                <Route
                  path="tracking"
                  element={
                    <ManagementGuard>
                      <PortalPlaceholder
                        testId="portal-tracking-page"
                        titleKey="portal.nav.tracking"
                        descKey="portal.pages.tracking_desc"
                        icon={ChartLineUp}
                      />
                    </ManagementGuard>
                  }
                />
                <Route
                  path="planning"
                  element={
                    <ManagementGuard>
                      <PortalPlanning />
                    </ManagementGuard>
                  }
                />
                <Route
                  path="ideas"
                  element={
                    <ManagementGuard>
                      <PortalInspirations />
                    </ManagementGuard>
                  }
                />
                <Route
                  path="ideas/:id"
                  element={
                    <ManagementGuard>
                      <PortalInspirationReader />
                    </ManagementGuard>
                  }
                />
                <Route
                  path="adonislinks"
                  element={
                    <ManagementGuard>
                      <PortalPlaceholder
                        testId="portal-adonislinks-page"
                        titleKey="portal.nav.adonislinks"
                        descKey="portal.pages.adonislinks_desc"
                        icon={LinkSimple}
                      />
                    </ManagementGuard>
                  }
                />
                <Route
                  path="leaks"
                  element={
                    <ManagementGuard>
                      <PortalLeaks />
                    </ManagementGuard>
                  }
                />
                <Route
                  path="leaks/:id"
                  element={
                    <ManagementGuard>
                      <PortalLeakDetail />
                    </ManagementGuard>
                  }
                />
                <Route
                  path="invoices"
                  element={<PortalInvoices />}
                />
                <Route
                  path="support"
                  element={
                    <PortalPlaceholder
                      testId="portal-support-page"
                      titleKey="portal.nav.support"
                      descKey="portal.pages.support_desc"
                      icon={Headset}
                    />
                  }
                />
                <Route
                  path="account"
                  element={
                    <PortalPlaceholder
                      testId="portal-account-page"
                      titleKey="portal.nav.account"
                      descKey="portal.pages.account_desc"
                      icon={UserCircle}
                    />
                  }
                />
              </Route>

              <Route
                path="/admin"
                element={
                  <ProtectedRoute role="admin">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminHome />} />
                <Route path="courses" element={<AdminCourses />} />
                <Route
                  path="courses/:track/:themeSlug"
                  element={<AdminTheme />}
                />
                <Route
                  path="courses/:track/:themeSlug/:chapterSlug"
                  element={<AdminChapterEditor />}
                />
                <Route path="resources" element={<AdminResources />} />
                <Route path="models" element={<AdminModels />} />
                <Route
                  path="models/:modelId"
                  element={<AdminModelLayout />}
                >
                  <Route index element={<Navigate to="profile" replace />} />
                  <Route path="profile" element={<AdminModelProfile />} />
                  <Route path="formation" element={<AdminModelFormation />} />
                  <Route path="notes" element={<AdminModelNotes />} />
                  <Route path="leaks" element={<AdminModelLeaks />} />
                  <Route
                    path="stats"
                    element={
                      <AdminModelTabPlaceholder
                        titleKey="models.tabs.stats"
                        icon={ChartLineUp}
                      />
                    }
                  />
                  <Route
                    path="planning"
                    element={<AdminModelPlanning />}
                  />
                  <Route
                    path="adonislink"
                    element={
                      <AdminModelTabPlaceholder
                        titleKey="models.tabs.adonislink"
                        icon={LinkSimple}
                      />
                    }
                  />
                  <Route
                    path="billing"
                    element={
                      <AdminModelTabPlaceholder
                        titleKey="models.tabs.billing"
                        icon={Receipt}
                      />
                    }
                  />
                  <Route
                    path="support"
                    element={
                      <AdminModelTabPlaceholder
                        titleKey="models.tabs.support"
                        icon={Headset}
                      />
                    }
                  />
                </Route>
                <Route
                  path="planning"
                  element={
                    <AdminPlaceholder
                      testId="admin-planning-page"
                      titleKey="admin.nav.planning"
                      descKey="admin.pages.planning_desc"
                      icon={Calendar}
                    />
                  }
                />
                <Route
                  path="ideas"
                  element={<AdminInspirations />}
                />
                <Route
                  path="ideas/:id"
                  element={<AdminInspirationEditor />}
                />
                <Route
                  path="leaks"
                  element={<AdminLeaks />}
                />
                <Route
                  path="leaks/:id"
                  element={<AdminLeakDetail />}
                />
                <Route path="inbox" element={<AdminInbox />} />
                <Route path="inbox/:id" element={<AdminInboxDetail />} />
                <Route
                  path="account"
                  element={
                    <AdminPlaceholder
                      testId="admin-account-page"
                      titleKey="admin.nav.account"
                      descKey="admin.pages.account_desc"
                      icon={UserCircle}
                    />
                  }
                />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Chrome>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
